import { useState, type FormEvent } from "react";
import { TRANSLATION_LOCALES, type TranslationLocale, type TranslationWorkItem } from "@mmm/contracts";
import { Button, Card, EmptyState, ErrorState, Select, setCatalogOverrides, SUPPORTED_LANGUAGES, Textarea, useLanguage, useT } from "@mmm/ui";
import { submitTranslationReview, submitTranslationSuggestion } from "../../api/commands.js";
import { getTranslationOverrides, listTranslationWork } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";

function statusKey(status: string) {
  if (status === "approved") return "translations.statusApproved" as const;
  if (status === "rejected") return "translations.statusRejected" as const;
  if (status === "stale") return "translations.statusStale" as const;
  return "translations.statusPending" as const;
}

export function TranslationRewards() {
  const t = useT();
  const { language } = useLanguage();
  const [locale, setLocale] = useState<TranslationLocale>(() => language === "en" ? "af" : language);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<TranslationWorkItem | null>(null);
  const [candidate, setCandidate] = useState("");
  const [reason, setReason] = useState("");
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [notice, setNotice] = useState<string | null>(null);
  const work = useQuery(listTranslationWork, { locale, offset });

  async function review(item: TranslationWorkItem, verdict: "approve" | "reject" | "unsure") {
    setBusy(item.suggestionId ?? item.messageKey);
    setNotice(null);
    try {
      const result = await submitTranslationReview({ locale, messageKey: item.messageKey, targetHash: item.targetHash, suggestionId: item.suggestionId, verdict });
      if (item.suggestionId && result.outcome === "approve") {
        const overlay = await getTranslationOverrides({ locale });
        setCatalogOverrides(locale, overlay.overrides);
      }
      setNotice(t("translations.reviewRecorded"));
    } catch { /* The command layer shows the error. */ }
    finally { setBusy(null); }
  }

  async function suggest(event: FormEvent) {
    event.preventDefault();
    if (!correcting || busy) return;
    setBusy(correcting.messageKey);
    setNotice(null);
    try {
      await submitTranslationSuggestion({ locale, messageKey: correcting.messageKey, targetHash: correcting.targetHash,
        candidate: candidate.trim(), reason: reason.trim(), requestId });
      setNotice(t("translations.correctionSubmitted"));
      setCorrecting(null);
      setCandidate("");
      setReason("");
      setRequestId(crypto.randomUUID());
    } catch { /* Keep the request ID stable for a safe retry. */ }
    finally { setBusy(null); }
  }

  function renderItem(item: TranslationWorkItem) {
    const itemBusy = busy === (item.suggestionId ?? item.messageKey);
    const reviewClosed = !work.data?.campaignEnabled || (!item.suggestionId && work.data.campaignRemaining === 0);
    return <Card key={item.suggestionId ?? item.messageKey} padding="sm">
      <strong>{item.messageKey}</strong>
      <p>{t("translations.uiContext")}: {item.context}</p>
      <p>{t("translations.englishSource")}: {item.english}</p>
      <p>{t("translations.currentTranslation")}: {item.current}</p>
      {item.candidate && <p>{t("translations.suggestedTranslation")}: {item.candidate}</p>}
      {item.reason && <p>{t("translations.reason")}: {item.reason}</p>}
      <div className="mmm-modal-actions">
        <Button variant="secondary" disabled={!!busy || reviewClosed} onClick={() => void review(item, "approve")}>{t(item.suggestionId ? "translations.betterTranslation" : "translations.accurate")}</Button>
        <Button variant="secondary" disabled={!!busy || reviewClosed} onClick={() => void review(item, "reject")}>{t(item.suggestionId ? "translations.keepCurrent" : "translations.needsCorrection")}</Button>
        <Button variant="secondary" disabled={!!busy || reviewClosed} onClick={() => void review(item, "unsure")}>{t("translations.unsure")}</Button>
        {!item.suggestionId && <Button variant="secondary" disabled={!!busy || !work.data?.campaignEnabled || (work.data?.campaignRemaining ?? 0) < 3} onClick={() => { setCorrecting(item); setCandidate(""); setReason(""); setRequestId(crypto.randomUUID()); }}>{t("translations.suggestCorrection")}</Button>}
      </div>
      {itemBusy && <p role="status">{t("translations.submitReview")}</p>}
    </Card>;
  }

  return <section>
    <h3>{t("translations.title")}</h3>
    <p>{t("translations.description")}</p>
    <p>{t("translations.rewardRule")} {t("translations.correctionRule")}</p>
    <p>{t("translations.rewardNotice")} {t("translations.plainTextOnly")}</p>
    <label className="mmm-field-label" htmlFor="translation-locale">{t("settings.language")}</label>
    <Select id="translation-locale" value={locale} onChange={(event) => {
      const next = event.target.value;
      if (!TRANSLATION_LOCALES.some((code) => code === next)) return;
      setLocale(next as TranslationLocale); setOffset(0); setCorrecting(null); setNotice(null);
    }}>
      {SUPPORTED_LANGUAGES.filter((item) => item.code !== "en").map((item) => <option key={item.code} value={item.code}>{item.nativeName}</option>)}
    </Select>
    {notice && <p role="status">{notice}</p>}
    {!!work.error && <ErrorState variant="list" title={t("errors.serverUnreachable")} onRetry={work.retry} />}
    {work.data && <>
      {!work.data.campaignEnabled && <p role="status">{t("translations.campaignPaused")}</p>}
      <p>{t("translations.campaignRemaining", { count: work.data.campaignRemaining })} {t("translations.qualifiedCount", { count: work.data.reviewReward.qualified })}</p>
      <h4>{t("translations.reviewSuggestions")}</h4>
      {work.data.suggestions.length ? work.data.suggestions.map(renderItem) : <EmptyState variant="list" title={t("translations.noSuggestions")} />}
      <h4>{t("translations.reviewExisting")}</h4>
      {work.data.items.length ? work.data.items.map(renderItem) : <EmptyState variant="list" title={t("translations.noExisting")} />}
      {work.data.hasMore && <Button variant="secondary" onClick={() => setOffset((value) => value + 15)}>{t("translations.moreTranslations")}</Button>}
      {correcting && <Card padding="sm">
        <h4>{t("translations.suggestCorrection")}</h4>
        <p>{correcting.english}</p>
        <p>{correcting.current}</p>
        <form onSubmit={(event) => void suggest(event)}>
          <label className="mmm-field-label" htmlFor="translation-candidate">{t("translations.yourCorrection")}</label>
          <Textarea id="translation-candidate" value={candidate} required maxLength={300} onChange={(event) => { setCandidate(event.target.value); setRequestId(crypto.randomUUID()); }} />
          <label className="mmm-field-label" htmlFor="translation-reason">{t("translations.reason")}</label>
          <Textarea id="translation-reason" value={reason} required minLength={10} maxLength={500} onChange={(event) => { setReason(event.target.value); setRequestId(crypto.randomUUID()); }} />
          <div className="mmm-modal-actions"><Button variant="primary" type="submit" disabled={!!busy || !work.data.campaignEnabled || work.data.campaignRemaining < work.data.acceptedCorrectionReward}>{t("translations.submitCorrection")}</Button></div>
        </form>
      </Card>}
      <h4>{t("translations.yourSuggestions")}</h4>
      {work.data.mySuggestions.map((item) => <p key={item.id}>{item.messageKey}: {t(statusKey(item.status))}</p>)}
    </>}
  </section>;
}
